import { c as _c } from "react/compiler-runtime";
/**
 * `useListIds(queryKey, rowKey)` — internal hook returning the stable
 * id array of a list query's cached rows.
 *
 * Implementation is a subscriber observer (`enabled: false` + sentinel
 * `queryFn`) on the SAME `queryKey` as the parent list hook, with a
 * `select` that extracts the row-id array. Structural sharing keeps
 * the array reference stable across cache patches that don't change
 * the id-set — so the consuming component re-renders only when rows
 * are added, removed, or reordered.
 *
 * Same observer pattern as `createItemsComponent`'s internal id-set
 * subscription — extracted here so both `<Items>` and the list hook's
 * `ids` field share one implementation. The codegen-emitted list
 * hook calls this once to expose `ids` on its return; consumers
 * driving their own virtualization (FlashList, SectionList) can read
 * `ids` instead of hand-rolling a `useSyncExternalStore` on the cache.
 */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { keepPreviousDataAcrossQueryKeys } from './query-placeholder-data.js';
import { createSubscriberQueryFn, getSubscriberQueryMetaOptions } from './subscriber-query-fn.js';
const EMPTY_IDS = Object.freeze([]);
export function useListIds(queryKey, rowKey) {
  const $ = _c(16);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient || $[1] !== queryKey) {
    t0 = createSubscriberQueryFn(queryClient, queryKey);
    $[0] = queryClient;
    $[1] = queryKey;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const queryFn = t0;
  let t1;
  if ($[3] !== rowKey) {
    t1 = data => {
      const rows = data?.data;
      if (rows === undefined || rows.length === 0) {
        return EMPTY_IDS;
      }
      const seen = new Set();
      const out = [];
      for (let i = 0; i < rows.length; i++) {
        const el = rows[i];
        const id = typeof el === "string" ? el : String(el[rowKey]);
        if (seen.has(id)) {
          continue;
        }
        seen.add(id);
        out.push(id);
      }
      return out;
    };
    $[3] = rowKey;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const selectIds = t1;
  let t2;
  if ($[5] !== queryClient || $[6] !== queryKey) {
    t2 = getSubscriberQueryMetaOptions(queryClient, queryKey);
    $[5] = queryClient;
    $[6] = queryKey;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== queryKey) {
    t3 = (previousData, previousQuery) => keepPreviousDataAcrossQueryKeys(queryKey, previousData, previousQuery);
    $[8] = queryKey;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== queryFn || $[11] !== queryKey || $[12] !== selectIds || $[13] !== t2 || $[14] !== t3) {
    t4 = {
      queryKey,
      queryFn,
      ...t2,
      enabled: false,
      staleTime: Number.POSITIVE_INFINITY,
      select: selectIds,
      placeholderData: t3
    };
    $[10] = queryFn;
    $[11] = queryKey;
    $[12] = selectIds;
    $[13] = t2;
    $[14] = t3;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  const result = useQuery(t4);
  return result.data ?? EMPTY_IDS;
}